import Link from 'next/link'
import Image from 'components/Image'
import { ResultTag, ResultTags } from 'components/ResultTag'
import PriceTag from 'components/PriceTag'
import VerifiedBadge from 'components/VerifiedBadge'
import Tooltip from 'components/Tooltip'
import UsdPrice from 'components/UsdPrice'
import AuctionPrice from './AuctionPrice'
import AuctionTag from './AuctionTag'
import { getAvatarURL, isAvatarVerified } from 'helpers/avatar'
import getTags from 'helpers/getTags'
import type { NFDAuctionAndPrice, NfdRecord } from 'api/api-client'

interface ResultProps {
  nfd: NfdRecord
  activeWallet: string | undefined
  auction: NFDAuctionAndPrice | undefined
}

export default function Result({ nfd, activeWallet = '', auction }: ResultProps) {
  const isOwnedByUser = nfd.owner === activeWallet
  const isForSale = nfd.state === 'forSale'
  const isReservedForUser = nfd.reservedFor === activeWallet
  const isReservedForSomeoneElse = !!nfd.reservedFor && nfd.reservedFor !== activeWallet
  const isInAuction = !!auction

  const renderTags = (type = 'desktop') => {
    if (isInAuction && type === 'mobile') {
      return (
        <div className="flex">
          <AuctionTag auction={auction} />
        </div>
      )
    }

    // reserved for someone else
    if (!isOwnedByUser && isReservedForSomeoneElse) {
      return (
        <div className="flex">
          <ResultTag label="Reserved" color="gray" />
        </div>
      )
    }

    // reserved for me
    if (isReservedForUser && type === 'mobile') {
      if (isForSale) {
        return (
          <div className="flex">
            <ResultTag label="Buy Now" brand />
          </div>
        )
      }

      return (
        <div className="flex">
          <ResultTag label="Claim Now" brand />
        </div>
      )
    }

    // manage sale
    if (isOwnedByUser && isForSale && type === 'mobile') {
      return (
        <div className="flex">
          <ResultTag label="Manage Sale" brand />
        </div>
      )
    }

    const tags = getTags(nfd, { premium: true })

    // only show first 2 tags on mobile
    const limit = type === 'mobile' ? 2 : 0

    return (
      <div className="flex flex-wrap">
        <ResultTags tags={tags} limit={limit} tagClassName="mb-1" />
      </div>
    )
  }

  const renderDesktopBadge = () => {
    if (isInAuction) {
      return (
        <div className="hidden md:flex w-full justify-end absolute p-3">
          <AuctionTag auction={auction} />
        </div>
      )
    }

    if (isReservedForUser) {
      return (
        <div className="hidden md:flex w-full justify-end absolute p-3">
          <ResultTag label="Claim Now" brand className="shadow-sm" />
        </div>
      )
    }

    if (isOwnedByUser && isForSale) {
      return (
        <div className="hidden md:flex w-full justify-end absolute p-3">
          <ResultTag label="Manage Sale" brand className="shadow-sm" />
        </div>
      )
    }

    if (isForSale && !isReservedForSomeoneElse) {
      return (
        <div className="hidden md:flex w-full justify-end absolute p-3">
          <ResultTag label="Buy Now" brand className="shadow-sm" />
        </div>
      )
    }

    return null
  }

  const renderVerifiedBadge = () => {
    const isVerified = isAvatarVerified(nfd)

    if (isVerified) {
      return (
        <>
          <div className="absolute z-10 bottom-0 right-0 -mr-12 -mb-12 scale-[0.4] sm:scale-50 md:hidden">
            <VerifiedBadge small />
          </div>
          <div className="hidden md:block absolute bottom-0 right-0 -mr-12 -mb-12">
            <Tooltip text="Verified avatar" direction="left" className="-translate-y-7">
              <div>
                <VerifiedBadge />
              </div>
            </Tooltip>
          </div>
        </>
      )
    }

    return null
  }

  const renderPrice = () => {
    if (!nfd.sellAmount || isReservedForSomeoneElse) {
      return null
    }

    return (
      <span className="flex flex-col justify-end">
        <span className="flex justify-end -mt-0.5">
          {isInAuction ? (
            <AuctionPrice auction={auction} className="md:text-base" />
          ) : (
            <PriceTag price={nfd.sellAmount} className="md:text-base" />
          )}
        </span>
        <UsdPrice price={nfd.sellAmount} className="text-right pr-2.5 mt-1 ml-3 md:ml-0" />
      </span>
    )
  }

  const renderContent = () => {
    return (
      <>
        <div className="shrink-0 w-16 sm:w-20 md:w-full mr-4 md:m-0 bg-gray-200 rounded-lg overflow-hidden">
          <div className="relative aspect-square">
            <Image
              src={getAvatarURL(nfd)}
              alt={nfd.name}
              className="group-hover:opacity-75 object-cover w-full h-full"
              objectFit="cover"
              layout="fill"
              fallbackSrc="/img/nfd-loading-placeholder_error.jpg"
              loadingSrc="/img/nfd-loading-placeholder.jpg"
            />
            {renderDesktopBadge()}
            {renderVerifiedBadge()}
          </div>
        </div>
        <div className="flex-1 flex items-start justify-between md:mt-3 min-w-0">
          <div className="min-w-0">
            <h3 className="text-md font-semibold text-gray-900 truncate">{nfd.name}</h3>
            <div className="md:hidden mt-1">{renderTags('mobile')}</div>
            <div className="hidden md:block mt-1">{renderTags()}</div>
          </div>
          {renderPrice()}
        </div>
      </>
    )
  }

  if (!isOwnedByUser && isReservedForSomeoneElse) {
    return <div className="flex items-center md:inline opacity-50">{renderContent()}</div>
  }

  return (
    <Link href="/name/[id]" as={`/name/${nfd.name}`} passHref>
      <a className="flex items-center md:inline group outline-brand-500 rounded-md">
        {renderContent()}
      </a>
    </Link>
  )
}
